import { ChevronDown, ChevronLeft, ChevronUp, EllipsisIcon, EyeOff, Heart, Share } from "lucide-react";
import React, { useEffect, useState } from "react";
import ImageGrid from "./ImageGrid";
import axiosInstance from "../../api/axiosInstance";
import { useSearchParams } from "react-router-dom";
// import { data } from "./staticData4";

const ZillowProperty = () => {
  const [searchParams] = useSearchParams();

  const contactId = searchParams.get("contactId");
  const locationId = searchParams.get("location");
  // const address = searchParams.get("address");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expandedDescription, setExpandedDescription] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.post(`api/webhooks/zillow/property`, {
          // full_address: decodeURIComponent(address),
          contact_id: contactId,
          location_id: locationId,
        });
        if (res?.data?.data?.zpid) {
          setData(res?.data?.data);
        } else {
          setError(true);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [contactId]);

  if (loading) {
    return <div className="h-screen flex items-center justify-center">Loading property...</div>;
  }

  if (error || !data) {
    return <div className="h-screen flex items-center justify-center text-red-500">Failed to load property</div>;
  }

  const getAtAGlanceFact = (facts, label) => {
    return facts?.find((item) => item.factLabel?.toLowerCase() === label?.toLowerCase())?.factValue;
  };

  const atAGlanceFacts = data?.resoFacts?.atAGlanceFacts;
  const locale = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    return num.toLocaleString();
  };

  const property = {
    type: getAtAGlanceFact(atAGlanceFacts, "Type") || null,
    yearBuild: getAtAGlanceFact(atAGlanceFacts, "Year Build") || data?.resoFacts?.yearBuilt || null,
    pricePerSquareFoot: locale(getAtAGlanceFact(atAGlanceFacts, "Price/sqft")) || locale(data?.resoFacts?.pricePerSquareFoot) || null,
    rentZestimate: locale(data?.resoFacts?.rentZestimate) || locale(data?.rentZestimate) || null,
    HOA: getAtAGlanceFact(atAGlanceFacts, "HOA") || null,
  };

  return (
    <div className="bg-gray-100 min-h-screen font-serif">
      {/* header */}
      <header className="bg-white w-full flex justify-center items-center">
        <div className="bg-white shadow-sm sticky top-0 z-20 border-b border-gray-200 grid grid-cols-3 px-8 py-4 w-full max-w-6xl mx-auto">
          <div className="flex justify-start">
            <div className="flex justify-center items-center space-x-1 cursor-pointer">
              <span>
                <ChevronLeft size={20} />
              </span>
              <span className="capitalize">back to search</span>
            </div>
          </div>
          <div className="flex justify-center items-center">
            <div className="max-w-32">
              <img className="bg-white object-contain w-full h-auto" src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/eb/Zillow_logo.svg/3840px-Zillow_logo.svg.png" alt="zillow-icon" />
            </div>
          </div>
          <div className="flex justify-end item-center space-x-4">
            <div className="flex justify-center items-center space-x-1 cursor-pointer">
              <span>
                <Heart size={20} />
              </span>
              <span className="capitalize">save</span>
            </div>
            <div className="flex justify-center items-center space-x-1 cursor-pointer">
              <span>
                <Share size={20} />
              </span>
              <span className="capitalize">share</span>
            </div>
            <div className="flex justify-center items-center space-x-1 cursor-pointer">
              <span>
                <EyeOff size={20} />
              </span>
              <span className="capitalize">hide</span>
            </div>
            <div className="flex justify-center items-center space-x-1 cursor-pointer">
              <span>
                <EllipsisIcon size={20} />
              </span>
              <span className="capitalize">more</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main */}
      <div className="max-w-6xl mx-auto px-8 py-4">
        <ImageGrid images={data?.responsivePhotosOriginalRatio?.map((o) => o?.mixedSources?.jpeg?.[0].url)} />
        <div className="grid grid-cols-12 py-4 gap-8">
          <div className="col-span-8">
            <div className="grid grid-cols-12 ">
              <div className="col-span-9 ">
                <div className="text-3xl font-semibold text-left">{data.price ? `$ ${Number(data.price).toLocaleString()}` : data.zestimate ? `$ ${Number(data.zestimate).toLocaleString()}` : "--"}</div>
                <div className="text-xl text-gray-600 text-left">
                  {data.address?.streetAddress}, {data.address?.city}, {data.address?.state} {data.address?.zipcode}
                </div>
              </div>
              <div className="col-span-1 ">
                <div className="text-3xl font-semibold text-center">{data.bedrooms}</div>
                <div className="text-xl text-gray-600 text-center">beds</div>
              </div>
              <div className="col-span-1 ">
                <div className="text-3xl font-semibold text-center">{data.bathrooms}</div>
                <div className="text-xl text-gray-600 text-center">baths</div>
              </div>
              <div className="col-span-1 ">
                <div className="text-3xl font-semibold text-right">{data.livingArea ? Number(data.livingArea).toLocaleString() : "--"}</div>
                <div className="text-xl text-gray-600 text-right">sqft</div>
              </div>
            </div>
            {property.rentZestimate && (
              <div className="bg-sky-200/50 rounded-md py-2 px-4 my-2 flex justify-center items-center w-max text-sm space-x-2">
                <span>Est.: </span> <span className="font-semibold">{property.rentZestimate}/mo</span>
                <span className="font-semibold text-blue-800">Get pre-qualified</span>
              </div>
            )}

            <div className="grid grid-cols-12 gap-4">
              {property.type && (
                <div className="col-span-4 bg-slate-200/60 px-4 py-4 flex justify-start items-center rounded-md text-xs space-x-2">
                  <div className="w-5">
                    <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" viewBox="0 0 32 32">
                      <path d="m28.34 7.06-14-5a1 1 0 0 0-.92.12A1 1 0 0 0 13 3v13.35l-2.33-2.09a1 1 0 0 0-1.34 0l-5.82 5.22A1.55 1.55 0 0 0 3 20.62v8.11A1.27 1.27 0 0 0 4.27 30H28a1 1 0 0 0 1-1V8a1 1 0 0 0-.66-.94M11 28H9v-3h2zm4 0h-2v-3.5a1.5 1.5 0 0 0-1.5-1.5h-3A1.5 1.5 0 0 0 7 24.5V28H5v-7.17l5-4.49 5 4.49zm12 0H17v-7.38a1.55 1.55 0 0 0-.51-1.14L15 18.12V4.42l12 4.29z"></path>
                      <path d="M24 13v10a1 1 0 0 1-2 0V13a1 1 0 0 1 2 0m-4-2v12a1 1 0 0 1-2 0V11a1 1 0 0 1 2 0"></path>
                    </svg>
                  </div>
                  <span>{property.type}</span>
                </div>
              )}
              {property.yearBuild && (
                <div className="col-span-4 bg-slate-200/60 px-4 py-4 flex justify-start items-center rounded-md text-xs space-x-2">
                  <div className="w-5">
                    <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" viewBox="0 0 32 32">
                      <path d="M15.48 25a1 1 0 0 0 1 1 1 1 0 1 0-1-1"></path>
                      <path d="M28.38 6a5 5 0 0 0-4-2h-.58A3 3 0 0 0 21 2h-9a3 3 0 0 0-3 3h-.06a2.52 2.52 0 0 0-2.46-2h-1A2.51 2.51 0 0 0 3 5.51v5A2.52 2.52 0 0 0 5.52 13h1a2.52 2.52 0 0 0 2.46-2H9a3 3 0 0 0 3 3h.88L12 25.35v.16a4.4 4.4 0 0 0 2.54 4.07A4.53 4.53 0 0 0 21 25.5v-.15L20.12 14H21a3 3 0 0 0 3-3v-1h.67l.8.26a2.5 2.5 0 0 0 .85.14A2.69 2.69 0 0 0 29 8.16 2.8 2.8 0 0 0 28.38 6M19 25.5a2.5 2.5 0 1 1-5 0l.87-11.5h3.22zm7.3-17.1h-.23l-.79-.27a2 2 0 0 0-.63-.1H23a1 1 0 0 0-1 1v2a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1V10a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v.48a.52.52 0 0 1-.52.52h-1a.52.52 0 0 1-.48-.52v-5A.52.52 0 0 1 5.52 5h1a.52.52 0 0 1 .48.52V6a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1 1 1 0 0 0 1 1h1.39a3 3 0 0 1 2.49 1.34.68.68 0 0 1-.58 1.06"></path>
                    </svg>
                  </div>
                  <span>Build in {property.yearBuild}</span>
                </div>
              )}
              {data.lotAreaValue && data.lotAreaUnits && (
                <div className="col-span-4 bg-slate-200/60 px-4 py-4 flex justify-start items-center rounded-md text-xs space-x-2">
                  <div className="w-5">
                    <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" viewBox="0 0 32 32">
                      <path d="m30.9 26.4-5.6-8a1 1 0 0 0-.8-.4H13v-2a4.7 4.7 0 0 0 4-4.5 4.3 4.3 0 0 0-1.6-3.3 4 4 0 1 0-6.8 0A4.3 4.3 0 0 0 7 11.5a4.7 4.7 0 0 0 4 4.4V18H7.5a1 1 0 0 0-.8.4l-5.6 8a1 1 0 0 0 .8 1.6h28.2a1 1 0 0 0 .8-1.6M9 11.5a2.3 2.3 0 0 1 1-1.8 2 2 0 0 0 .3-2.6A2 2 0 0 1 10 6a2 2 0 0 1 4 0 2 2 0 0 1-.3 1 2 2 0 0 0 .4 2.7 2.3 2.3 0 0 1 .9 1.8 2.6 2.6 0 0 1-2 2.4V11a1 1 0 0 0-2 0v2.9a2.6 2.6 0 0 1-2-2.4M3.8 26 8 20h3v2a1 1 0 0 0 2 0v-2h11l4.2 6z"></path>
                    </svg>
                  </div>
                  <span>
                    {locale(data.lotAreaValue?.toFixed(2))} {data.lotAreaUnits}
                  </span>
                </div>
              )}
              {data.zestimate && (
                <div className="col-span-4 bg-slate-200/60 px-4 py-4 flex justify-start items-center rounded-md text-xs space-x-2">
                  <div className="w-5">
                    <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" viewBox="0 0 32 32">
                      <path d="M21.549 9.663a.96.96 0 0 0-.982-.368c-2.123.479-9.93 2.59-17.658 6.306v-3.508c0-.568.26-1.104.705-1.457 3.197-2.525 11.13-8.782 11.13-8.782a1.86 1.86 0 0 1 2.306 0l10.881 8.599.23.183c.444.353.703.895.703 1.459l-.003-.004V16.4c-3.454.478-10.626 2.183-13.745 3.339-.028.01-.06.003-.018-.044 1.753-2.009 4.802-4.76 7.615-6.828a.956.956 0 0 0 .206-1.34z"></path>
                      <path d="M9.12 25.7a.95.95 0 0 0 1.098.264v.003c4.972-2.084 14.858-4.533 18.645-5.068v6.7a1.86 1.86 0 0 1-1.86 1.855H4.77c-1.03 0-1.86-.83-1.86-1.857v-8.305c2.828-1.386 10.197-4.117 12.74-4.803.05-.013.062.017.017.047-2.074 1.379-6.585 6.26-8.094 8.003a.96.96 0 0 0-.025 1.226c.45.562 1.118 1.396 1.572 1.934Z"></path>
                    </svg>
                  </div>
                  <span>
                    $ {locale(data.zestimate)} Zestimate<sup>®</sup>
                  </span>
                </div>
              )}
              {property.pricePerSquareFoot && (
                <div className="col-span-4 bg-slate-200/60 px-4 py-4 flex justify-start items-center rounded-md text-xs space-x-2">
                  <div className="w-5">
                    <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" viewBox="0 0 32 32">
                      <path d="M29 21H11V3a1.003 1.003 0 0 0-1-1H3a1.003 1.003 0 0 0-1 1v26a1.003 1.003 0 0 0 1 1h26a1.003 1.003 0 0 0 1-1v-7a1.003 1.003 0 0 0-1-1m-1 7H4V4h5v2H8a1 1 0 0 0 0 2h1v3H8a1 1 0 0 0 0 2h1v3H8a1 1 0 0 0 0 2h1v5h5v1a1 1 0 0 0 2 0v-1h3v1a1 1 0 0 0 2 0v-1h3v1a1 1 0 0 0 2 0v-1h2z"></path>
                      <path d="M20.038 14.587a2.88 2.88 0 0 1-1.947-.831 1.35 1.35 0 0 0-.926-.419.802.802 0 0 0-.865.825 1.58 1.58 0 0 0 .595 1.141 4.03 4.03 0 0 0 2.19.883v.885a.929.929 0 0 0 1.858 0v-.913a2.95 2.95 0 0 0 2.757-2.875c0-1.385-.838-2.23-2.54-2.562l-1.278-.263c-.858-.17-1.25-.494-1.25-1 0-.568.506-1.02 1.303-1.02a2.3 2.3 0 0 1 1.737.763 1.4 1.4 0 0 0 .98.419.75.75 0 0 0 .777-.757 1.62 1.62 0 0 0-.58-1.136 3.7 3.7 0 0 0-1.907-.856v-.943a.929.929 0 0 0-1.857 0v.927a2.834 2.834 0 0 0-2.67 2.772c0 1.378.824 2.257 2.46 2.595l1.27.277c.974.21 1.359.507 1.359 1.014 0 .649-.514 1.074-1.466 1.074"></path>
                    </svg>
                  </div>
                  <span>{property.pricePerSquareFoot}/sqft</span>
                </div>
              )}
              {property.HOA ||
                (property.rentZestimate && (
                  <div className="col-span-4 bg-slate-200/60 px-4 py-4 flex justify-start items-center rounded-md text-xs space-x-2">
                    <div className="w-5">
                      <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" viewBox="0 0 32 32">
                        <path d="m21.65 6.47-5-4.23a1 1 0 0 0-1.3 0l-5 4.23a1 1 0 0 0-.35.76v6.27a1.5 1.5 0 0 0 1.5 1.5h9a1.5 1.5 0 0 0 1.5-1.5V7.23a1 1 0 0 0-.35-.76M20 13h-3v-2a1 1 0 0 0-2 0v2h-3V7.69l4-3.38 4 3.38z"></path>
                        <path d="m14.31 20.81-3.94-4.75-.09-.1A2.9 2.9 0 0 0 8 15.2v-2.7C8 9.92 6.38 9 5 9s-3 1.08-3 3.5v7.2a3.74 3.74 0 0 0 .82 2.3L7 27.25V29a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-6.28a3 3 0 0 0-.69-1.91M13 28H9v-1.1a1 1 0 0 0-.22-.62l-4.4-5.51A1.67 1.67 0 0 1 4 19.7v-7.2c0-.68.17-1.5 1-1.5.25 0 1 0 1 1.5v3.85A3 3 0 0 0 6.23 20l3 3.6a1 1 0 0 0 1.54-1.28l-3-3.64a1 1 0 0 1-.06-1.28.86.86 0 0 1 1.2 0l3.89 4.68a1 1 0 0 1 .23.64zM27 9c-1.38 0-3 .92-3 3.5v2.69a2.93 2.93 0 0 0-2.28.77l-.09.1-3.94 4.75a3 3 0 0 0-.69 1.91V29a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-1.75L29.18 22a3.74 3.74 0 0 0 .82-2.3v-7.2c0-2.42-1.51-3.5-3-3.5m1 10.7a1.7 1.7 0 0 1-.38 1.08l-4.4 5.5a1 1 0 0 0-.22.62V28h-4v-5.28a1 1 0 0 1 .23-.64l3.89-4.68a.84.84 0 0 1 1.14 0 1 1 0 0 1 0 1.38l-3 3.6a1 1 0 1 0 1.54 1.28l3-3.56a3 3 0 0 0 .2-3.73V12.5c0-1.5.75-1.5 1-1.5.83 0 1 .82 1 1.5z"></path>
                      </svg>
                    </div>
                    <span>{property.HOA ? property.HOA : property.rentZestimate && `${property.rentZestimate} Estimated rent`}</span>
                  </div>
                ))}
            </div>

            <hr className="my-8 border-gray-300" />
            <div>
              <h2 className="text-3xl font-semibold text-left mb-4">Home value</h2>
              <div className="grid grid-cols-12 gap-4">
                {data.zestimate && (
                  <div className="col-span-4">
                    <div className="bg-gray-200/70 rounded-md px-4 py-3 min-w-max flex justify-center items-center flex-col">
                      <p class="border-b border-dashed border-gray-600">
                        Zestimate<sup>®</sup>
                      </p>
                      <p className="font-semibold">${Number(data.zestimate).toLocaleString()}</p>
                    </div>
                  </div>
                )}
                {/* {data.zestimate && (
                <div className="col-span-4">
                  <div className="bg-gray-200/70 rounded-md px-4 py-3 min-w-max flex justify-center items-center flex-col">
                    <p class="border-b border-dashed border-gray-600">
                      Estimated sales range
                    </p>
                    <p className="font-semibold">${Number(data.zestimate).toLocaleString()}</p>
                  </div>
                </div>
              )} */}
                {data.rentZestimate && (
                  <div className="col-span-4">
                    <div className="bg-gray-200/70 rounded-md px-4 py-3 min-w-max flex justify-center items-center flex-col">
                      <p class="border-b border-dashed border-gray-600">
                        Rend Zestimate<sup>®</sup>
                      </p>
                      <p className="font-semibold">${Number(data.rentZestimate).toLocaleString()}/mo</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <p class="font-semibold text-xl text-left py-3">
              Help make your{" "}
              <span class="text-[#136f65]">
                Zestimate<sup>®</sup>
              </span>{" "}
              more accurate
            </p>

            <hr className="my-8 border-gray-300" />
            <div>
              <h2 className="text-4xl font-semibold text-left">What's special</h2>
              <div className="flex flex-row flex-wrap  justify-start items-start gap-4 py-4">
                {data?.resoFacts?.appliances?.length > 0 &&
                  data.resoFacts.appliances.map((appliance, index) => (
                    <div key={index} className="uppercase bg-slate-200/80 px-4 py-2 w-max rounded-md text-xs font-medium">
                      {appliance}
                    </div>
                  ))}

                <p className={`text-left font-normal text-sm whitespace-pre-line ${!expandedDescription ? "line-clamp-4" : ""}`}>{data.description}</p>

                <button onClick={() => setExpandedDescription(!expandedDescription)} className="text-blue-700 text-sm mt-2 font-semibold">
                  {expandedDescription ? (
                    <span className="flex justify-center items-center space-x-1">
                      <ChevronUp strokeWidth={2.5} size={20} /> <span>Hide</span>
                    </span>
                  ) : (
                    <span className="flex justify-center items-center space-x-1">
                      <ChevronDown strokeWidth={2.5} size={20} /> <span>Show more</span>
                    </span>
                  )}
                </button>
              </div>
            </div>

            <hr className="my-8 border-gray-300" />
            <div>
              <h2 className="text-2xl font-semibold text-left py-4">Facts & features</h2>
              <div className="">
                <h3 className="text-xl font-semibold text-left bg-gray-200/70 px-4 py-2">Interior</h3>
              </div>
            </div>
          </div>
          <div className="col-span-4 flex justify-center h-max">
            <div className="border border-slate-400 px-4 py-4 rounded-md w-full ">
              <button className="bg-blue-600 text-white px-4 py-2 cursor-pointer hover:bg-blue-700 rounded-md w-full min-w-24">Contact agent</button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      {/* <div class="Spacer-c11n-8-111-2__sc-17suqs2-0 styles__StyledFooterNav-fshdp-8-112-0__sc-1msbtd7-7 gXwCvF hvjczd">
        <div>
          <footer class="pfs__sc-16g5ked-0 gjgeMG site-footer">
            <div class="pfs__cacbnh-0 kGTZeg">
              <nav class="pfs__sc-1i1gn38-0 ekiOUH">
                <ul data-za-category="Navigation" data-za-action="Footer" class="pfs__sc-1kahb5i-0 daFyfs">
                  <li>
                    <a target="_blank" href="https://www.zillow.com/z/corp/about/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="About">
                      About
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.com/zestimate/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Zestimates">
                      Zestimates
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.com/news/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="News">
                      News
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.com/research/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Research">
                      Research
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.com/careers/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Careers">
                      Careers
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.com/careers/global-applicant-privacy-notice/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Applicant Privacy Notice">
                      Applicant Privacy Notice
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.comhttps://www.zillow.help/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Help">
                      Help
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.com/partners/advertise/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Advertise">
                      Advertise
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.comhttps://www.zillow.com/rental-manager/resources/fair-housing-guide/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Fair Housing Guide">
                      Fair Housing Guide
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.comhttps://advocacy.zillowgroup.com/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Advocacy">
                      Advocacy
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.com/z/corp/terms/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Terms of use">
                      Terms of use
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.comhttps://www.zillowgroup.com/zg-privacy-policy/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Privacy Notice">
                      Privacy Notice
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.comhttps://www.zillowgroup.com/zg-privacy-policy/#third-party" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Ad Choices">
                      Ad Choices
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.comjavascript:;" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh cookie-preference" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Cookie Preference">
                      Cookie Preference
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.com/learn/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Learn">
                      Learn
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.com/tech/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="AI">
                      AI
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.com/buy/app-download/" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Mobile Apps">
                      Mobile Apps
                    </a>
                  </li>
                </ul>
                <ul class="pfs__sc-1kahb5i-0 daFyfs brand-links" data-za-category="Navigation" data-za-action="Footer">
                  <li>
                    <a target="_blank" href="https://www.zillow.comhttps://www.trulia.com/" rel="noopener noreferrer" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Trulia">
                      Trulia
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.comhttps://streeteasy.com/" rel="noopener noreferrer" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="StreetEasy">
                      StreetEasy
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.comhttps://hotpads.com/" rel="noopener noreferrer" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="HotPads">
                      HotPads
                    </a>
                  </li>
                  <li>
                    <a target="_blank" href="https://www.zillow.comhttps://outeast.com/" rel="noopener noreferrer" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 dgPSDh" tabindex="0" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Out East">
                      Out East
                    </a>
                  </li>
                </ul>
                <div class="pfs__sc-16y5ofh-0 fPZEBj">
                  <a id="doNotSell" href="https://privacy.zillowgroup.com/cookie" aria-label="Do not sell or share my personal information" class="StyledTextButton-c11n-8-111-2__sc-1nwmfqo-0 cuEMbG">
                    Do Not Sell or Share My Personal Information<span aria-hidden="true"> →</span>
                  </a>
                </div>
              </nav>
            </div>
            <div class="pfs__wsq7ni-0 hAtGYA">
              <div class="pfs__sc-1sj5qgb-0 eQDVAr">
                <p class="Text-c11n-8-111-2__sc-aiai24-0 jhVaQA">
                  Zillow Group is committed to ensuring digital accessibility for individuals with disabilities. We are continuously working to improve the accessibility of our web experience for everyone, and we welcome feedback and accommodation requests. If you wish to report an issue or seek an accommodation, please
                  <a target="_blank" href="https://www.zillow.comhttps://www.zillow.help/contact-us" rel="nofollow" class="Anchor-c11n-8-111-2__sc-hn4bge-0 bgDMJi">
                    let us know
                  </a>
                  .
                </p>
              </div>
              <div class="pfs__sc-1sj5qgb-0 eQDVAr">
                <p class="Text-c11n-8-111-2__sc-aiai24-0 jhVaQA">
                  <a target="_blank" href="https://www.zillow.comhttps://www.zillow.com/affiliated-business-arrangement-disclosure/" rel="nofollow" class="Anchor-c11n-8-111-2__sc-hn4bge-0 bgDMJi">
                    Affiliated Business Arrangement Disclosure
                  </a>
                  <br />
                  Zillow, Inc. holds real estate brokerage
                  <a target="_blank" href="https://www.zillow.com/c/info/real-estate-licenses/" class="Anchor-c11n-8-111-2__sc-hn4bge-0 bgDMJi">
                    licenses
                  </a>{" "}
                  in multiple states. Zillow (Canada), Inc. holds real estate brokerage
                  <a target="_blank" href="https://www.zillow.com/c/info/real-estate-licenses/" class="Anchor-c11n-8-111-2__sc-hn4bge-0 bgDMJi">
                    licenses
                  </a>{" "}
                  in multiple provinces.
                  <br />
                  All mortgage lending products and information provided by Zillow Home Loans, LLC, NMLS #10287.
                  <a target="_blank" href="https://www.zillow.comhttps://www.nmlsconsumeraccess.org" class="Anchor-c11n-8-111-2__sc-hn4bge-0 bgDMJi">
                    NMLS Consumer Access
                  </a>
                  <br />
                  Licensed Mortgage Banker, N.Y.S. Department of Financial Services
                  <br />
                  2600 Michelson Drive, Suite 1201, Irvine, CA 92612 | Equal Housing Lender |
                  <a target="_blank" href="https://www.zillow.comhttps://www.zillow.com/homeloans/licensing/" class="Anchor-c11n-8-111-2__sc-hn4bge-0 bgDMJi">
                    Licensing Information
                  </a>
                  <br />
                  <a target="_blank" href="https://www.zillow.comhttps://www.zillowstatic.com/s3/pfs/static/SOP_NYS_10-4-23.pdf" rel="nofollow" class="Anchor-c11n-8-111-2__sc-hn4bge-0 bgDMJi">
                    § 442-H New York Standard Operating Procedures
                  </a>
                  <br />
                  <a target="_blank" href="https://www.zillow.comhttps://www.zillowstatic.com/s3/pfs/static/fairhousingnotice_10-4-23.pdf" rel="nofollow" class="Anchor-c11n-8-111-2__sc-hn4bge-0 bgDMJi">
                    § New York Fair Housing Notice
                  </a>
                  <br />
                  TREC:
                  <a target="_blank" href="https://www.zillow.comhttps://delivery.digitallibrary.zillowgroup.com/public/TRECNO.IABS1-1INFORMATIONABOUTBROKERAGESERVICES_pdf_Original.pdf" rel="nofollow" class="Anchor-c11n-8-111-2__sc-hn4bge-0 bgDMJi">
                    Information about brokerage services
                  </a>
                  ,
                  <a target="_blank" href="https://www.zillow.comhttps://www.trec.texas.gov/forms/consumer-protection-notice" rel="nofollow" class="Anchor-c11n-8-111-2__sc-hn4bge-0 bgDMJi">
                    Consumer protection notice
                  </a>
                  <br />
                  California DRE #1522444
                  <a target="_blank" href="https://www.zillow.comhttps://www.zillow.com/c/info/contact-us/" class="Anchor-c11n-8-111-2__sc-hn4bge-0 pfs__chkjii-0 bgDMJi jkCZYU">
                    Contact Zillow, Inc. Brokerage
                  </a>
                </p>
              </div>
              <div class="pfs__sc-1sj5qgb-0 eQDVAr">
                <p class="Text-c11n-8-111-2__sc-aiai24-0 jhVaQA">For listings in Canada, the trademarks REALTOR®, REALTORS®, and the REALTOR® logo are controlled by The Canadian Real Estate Association (CREA) and identify real estate professionals who are members of CREA. The trademarks MLS®, Multiple Listing Service® and the associated logos are owned by CREA and identify the quality of services provided by real estate professionals who are members of CREA. Used under license.</p>
              </div>
              <ul class="pfs__sc-5sfc2u-0 giDSWJ">
                <li>
                  <a target="_blank" href="https://www.zillow.comhttps://www.zillow.com/buy/app-download/?itc=zw_zw_zw_zillow-footer_btn_ios-download" aria-label="Download on the App Store">
                    <img src="https://www.zillowstatic.com/s3/pfs/static/app-store-badge.svg" type="image/svg+xml" alt="App store logo" focusable="false" height="32" width="96" class="" title="Download on the App Store" loading="lazy" decoding="async" />
                  </a>
                </li>
                <li>
                  <a target="_blank" href="https://www.zillow.comhttps://www.zillow.com/buy/app-download/?itc=zw_zw_zw_zillow-footer_btn_android-download" aria-label="Get it on Google play">
                    <img src="https://www.zillowstatic.com/s3/pfs/static/google-play-badge.svg" type="image/svg+xml" alt="Google play logo" focusable="false" height="32" width="108" class="" title="Get it on Google Play" loading="lazy" decoding="async" />
                  </a>
                </li>
              </ul>
              <ul class="pfs__sc-1a1yrk2-0 fOehGK">
                <li>
                  <a target="_blank" href="https://www.zillow.com/" width="152" height="32" data-za-action="Zillow logo click" data-za-category="!inherit" aria-label="Zillow.com homepage" version="DEFAULT" class="Anchor-c11n-8-111-2__sc-hn4bge-0 pfs__j60ma-0 bgDMJi jnGzmK znav-topnav-logo">
                    <img src="https://www.zillowstatic.com/s3/pfs/static/z-logo-default.svg" type="image/svg+xml" alt="Zillow logo" focusable="false" height="32" width="152" class="pfs__j60ma-1 dIfpOE" loading="eager" decoding="auto" />
                  </a>
                </li>
                <li>
                  <div id="socialLinks" data-za-category="Homepage" data-za-action="Social Icon" class="pfs__sc-1l86zl0-0 jlVbYk">
                    <span>Follow us:</span>
                    <a target="_blank" href="https://www.zillow.comhttp://www.facebook.com/Zillow" rel="nofollow noopener noreferrer" class="Anchor-c11n-8-111-2__sc-hn4bge-0 pfs__chkjii-1 bgDMJi WzpHc" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Facebook" aria-label="Visit us on Facebook">
                      <span aria-hidden="true" class="pfs__tgmoyx-0 KBJPo">
                        <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false" role="img" class="Icon-c11n-8-111-2__sc-13llmml-0 jtbjVn">
                          <path stroke="none" d="M30,16.08A14,14,0,1,0,13.81,30V20H10.26V16h3.55V13c0-3.53,2.09-5.48,5.29-5.48a20.46,20.46,0,0,1,3.13.28v3.46H20.47A2,2,0,0,0,18.2,13a1.7,1.7,0,0,0,0,.45V16h3.88l-.62,4H18.19V30A14.06,14.06,0,0,0,30,16.08Z"></path>
                        </svg>
                      </span>
                      <span class="VisuallyHidden-c11n-8-111-2__sc-t8tewe-0 bARXoy">Visit us on facebook</span>
                    </a>
                    <a target="_blank" href="https://www.zillow.comhttps://www.instagram.com/zillow" rel="nofollow noopener noreferrer" class="Anchor-c11n-8-111-2__sc-hn4bge-0 pfs__chkjii-1 bgDMJi WzpHc" data-za-category="!inherit" data-za-action="!inherit" data-za-label="Instagram" aria-label="Visit us on Instagram">
                      <span aria-hidden="true" class="pfs__tgmoyx-0 gPapVq">
                        <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false" role="img" class="Icon-c11n-8-111-2__sc-13llmml-0 jtbjVn">
                          <g stroke="none">
                            <path d="M21,2H11a9,9,0,0,0-9,9V21a9,9,0,0,0,9,9H21a9,9,0,0,0,9-9V11A9,9,0,0,0,21,2Zm7,19a7,7,0,0,1-7,7H11a7,7,0,0,1-7-7V11a7,7,0,0,1,7-7H21a7,7,0,0,1,7,7Z"></path>
                            <path d="M16,9a7,7,0,1,0,7,7A7,7,0,0,0,16,9Zm0,12a5,5,0,1,1,5-5A5,5,0,0,1,16,21Z"></path>
                            <circle cx="23.5" cy="8.5" r="1.5"></circle>
                          </g>
                        </svg>
                      </span>
                      <span class="VisuallyHidden-c11n-8-111-2__sc-t8tewe-0 bARXoy">Visit us on instagram</span>
                    </a>
                    <a target="_blank" href="https://www.zillow.comhttps://www.tiktok.com/@zillow" rel="nofollow noopener noreferrer" class="Anchor-c11n-8-111-2__sc-hn4bge-0 pfs__chkjii-1 bgDMJi WzpHc" data-za-category="!inherit" data-za-action="!inherit" data-za-label="TikTok" aria-label="Visit us on TikTok">
                      <span aria-hidden="true" class="pfs__tgmoyx-0 ekSLRl">
                        <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false" role="img" class="Icon-c11n-8-111-2__sc-13llmml-0 jtbjVn">
                          <path d="M27.988 9.01a6.774 6.774 0 0 1-4.16-1.424 7.095 7.095 0 0 1-2.616-4.263A7.263 7.263 0 0 1 21.079 2h-4.46v12.492l-.006 6.843c0 1.83-1.162 3.38-2.773 3.926a3.961 3.961 0 0 1-3.345-.377 4.158 4.158 0 0 1-1.973-3.487c-.034-2.313 1.79-4.199 4.045-4.199.445 0 .873.075 1.273.21v-4.641a8.565 8.565 0 0 0-1.286-.098c-2.468 0-4.777 1.052-6.427 2.947a8.687 8.687 0 0 0-2.11 5.173c-.152 2.516.746 4.908 2.489 6.673A8.423 8.423 0 0 0 12.555 30a8.448 8.448 0 0 0 6.048-2.538 8.664 8.664 0 0 0 2.505-6.092l-.024-10.218a11.2 11.2 0 0 0 2.506 1.498 11.03 11.03 0 0 0 4.41.911V9.01h-.012z" stroke="none"></path>
                        </svg>
                      </span>
                      <span class="VisuallyHidden-c11n-8-111-2__sc-t8tewe-0 bARXoy">Visit us on tiktok</span>
                    </a>
                  </div>
                </li>
                <li>
                  <span class="VisuallyHidden-c11n-8-111-2__sc-t8tewe-0 bARXoy">© 2006 to 2026 Zillow</span>
                  <span aria-hidden="true">© 2006-2026 Zillow</span>
                  <span>
                    <a target="_blank" href="https://www.zillow.comhttps://www.hud.gov/program_offices/fair_housing_equal_opp" rel="nofollow noopener noreferrer" id="eho-link" class="Anchor-c11n-8-111-2__sc-hn4bge-0 pfs__x86ldc-0 bgDMJi hwOTvn">
                      <svg viewBox="0 0 271.2 290.1" aria-hidden="true" focusable="false" role="img" class="Icon-c11n-8-111-2__sc-13llmml-0 jtbjVn">
                        <g stroke="none">
                          <path d="M0 126.8V90.3l10.4-7L30 70l19.2-12.7 19.5-13.2 19.1-12.8 18.8-12.6L133.8.5a3.4 3.4 0 0 1 3.6 0l5.8 3.9 13.7 9.3q13.2 9 26.5 17.8l13.8 9.3 27.4 18.4 26.2 17.6 18.4 12.4a17.7 17.7 0 0 1 2 1.8v36h-17.6v107.8H252l-147-.3H19.8a16.2 16.2 0 0 1-2-.3V126.8zm224.4 71v-.7-89.8a1.8 1.8 0 0 0-.8-1.3L198 88.8l-41-27.5L136 47.5a1.2 1.2 0 0 0-1.6 0l-7.8 5.3-18.5 12.4L91 76.8 73.4 88.6l-25.2 16.9a1.9 1.9 0 0 0-1 1.8v90.5z"></path>
                          <path d="M89.1 181v-38.6h93.4V181zM88.9 97.8h93.3v38.5H89z"></path>
                          <path d="M21.4 261v-20.3h14.7v2.4h-12v6.2h11.3v2.4H24v7h12.5v2.3zM55.7 258.9a16.5 16.5 0 0 0 3.5 1.8l-.8 2A15.9 15.9 0 0 1 54 260a10 10 0 0 1-5 1.3 9.6 9.6 0 0 1-5-1.4 8.9 8.9 0 0 1-3.4-3.7 12 12 0 0 1-1.3-5.4 12.2 12.2 0 0 1 1.3-5.5 8.6 8.6 0 0 1 3.4-3.8 10 10 0 0 1 5-1.3 9.8 9.8 0 0 1 5.1 1.4 8.7 8.7 0 0 1 3.5 3.7 12.1 12.1 0 0 1 1.2 5.5 12.6 12.6 0 0 1-.8 4.5 9.5 9.5 0 0 1-2.3 3.5zm-6-3.5a10 10 0 0 1 4 2q2.3-2.2 2.3-6.5a10.3 10.3 0 0 0-.8-4.4 6.5 6.5 0 0 0-2.5-2.8 7 7 0 0 0-8.6 1q-2 2-2 6.2a8.6 8.6 0 0 0 2 6 6.5 6.5 0 0 0 5 2.2 6.9 6.9 0 0 0 2.7-.6 8.7 8.7 0 0 0-2.7-1.1zM75.7 240.7h2.7v11.7a14 14 0 0 1-.7 5 6 6 0 0 1-2.5 2.8 8.9 8.9 0 0 1-4.7 1.2 9.8 9.8 0 0 1-4.7-1 5.6 5.6 0 0 1-2.6-2.9 13.6 13.6 0 0 1-.8-5v-11.8h2.7v11.7a11.4 11.4 0 0 0 .5 4 3.7 3.7 0 0 0 1.7 1.9 5.9 5.9 0 0 0 3 .7q3 0 4.2-1.4t1.2-5.2zM80.7 261l7.8-20.3h2.9l8.3 20.3h-3l-2.4-6.1h-8.5l-2.3 6.1zm5.8-8.3h7l-2.2-5.6q-1-2.6-1.4-4.3a26 26 0 0 1-1.1 4zM101.7 261v-20.3h2.7v18h10v2.3zM124.6 261v-20.3h2.7v8.4h10.5v-8.4h2.7V261h-2.7v-9.6h-10.5v9.6zM144.2 251.1q0-5 2.7-7.9a9.2 9.2 0 0 1 7-2.9 9.7 9.7 0 0 1 5 1.4 8.8 8.8 0 0 1 3.5 3.7 12.2 12.2 0 0 1 1.2 5.5 12 12 0 0 1-1.2 5.5 8.5 8.5 0 0 1-3.6 3.7 10.1 10.1 0 0 1-5 1.3 9.6 9.6 0 0 1-5-1.4 9 9 0 0 1-3.5-3.8 11.5 11.5 0 0 1-1.1-5zm2.7 0a8.1 8.1 0 0 0 2 5.9 6.9 6.9 0 0 0 10 0 8.6 8.6 0 0 0 2-6.1 10.4 10.4 0 0 0-.9-4.4 6.4 6.4 0 0 0-2.5-2.8 6.7 6.7 0 0 0-3.6-1 6.9 6.9 0 0 0-4.9 2q-2 1.9-2 6.5zM180.4 240.7h2.7v11.7a14 14 0 0 1-.7 5 6 6 0 0 1-2.5 2.8 8.9 8.9 0 0 1-4.7 1.2 9.8 9.8 0 0 1-4.7-1 5.6 5.6 0 0 1-2.6-2.9 13.6 13.6 0 0 1-.8-5v-11.8h2.7v11.7a11.4 11.4 0 0 0 .5 4 3.7 3.7 0 0 0 1.7 1.9 5.9 5.9 0 0 0 3 .7q2.9 0 4.2-1.4t1.2-5.2zM186.7 254.5l2.5-.2a5.6 5.6 0 0 0 .9 2.5 4.6 4.6 0 0 0 2 1.6 7.7 7.7 0 0 0 3.1.6 7.5 7.5 0 0 0 2.7-.5 3.8 3.8 0 0 0 1.8-1.2 3 3 0 0 0 .6-1.8 2.6 2.6 0 0 0-.6-1.7 4.1 4.1 0 0 0-1.8-1.2 35.7 35.7 0 0 0-3.6-1 18.4 18.4 0 0 1-4-1.2 5.5 5.5 0 0 1-2.1-2 4.6 4.6 0 0 1-.7-2.4 5.2 5.2 0 0 1 .8-3 5.4 5.4 0 0 1 2.6-2 9.7 9.7 0 0 1 3.7-.6 10.2 10.2 0 0 1 4 .7 5.8 5.8 0 0 1 2.7 2.1 6.2 6.2 0 0 1 1 3.2l-2.6.2a4.2 4.2 0 0 0-1.4-2.9 5.5 5.5 0 0 0-3.6-1 5.7 5.7 0 0 0-3.5 1 2.7 2.7 0 0 0-1.2 2 2.3 2.3 0 0 0 .8 1.9q.8.7 4 1.4a26.7 26.7 0 0 1 4.6 1.3 6.2 6.2 0 0 1 2.6 2.1 5 5 0 0 1 .9 2.9 5.5 5.5 0 0 1-1 3 6.1 6.1 0 0 1-2.6 2.3 9.3 9.3 0 0 1-4 .8 11.7 11.7 0 0 1-4.6-.8 6.5 6.5 0 0 1-3-2.4 6.9 6.9 0 0 1-1-3.7zM207 261v-20.3h2.7V261zM214.4 261v-20.3h2.8l10.6 16v-16h2.6V261h-2.7L217 245v16zM244.4 253v-2.3h8.7v7.5a15.2 15.2 0 0 1-4.1 2.4 12.1 12.1 0 0 1-4.4.8 11.6 11.6 0 0 1-5.4-1.3 8.5 8.5 0 0 1-3.7-3.7 11.7 11.7 0 0 1-1.2-5.4 12.6 12.6 0 0 1 1.2-5.6 8.3 8.3 0 0 1 3.6-3.8 11.2 11.2 0 0 1 5.3-1.2 10.6 10.6 0 0 1 4 .7 6.6 6.6 0 0 1 2.8 2 8.8 8.8 0 0 1 1.6 3.3l-2.5.7a7.5 7.5 0 0 0-1.1-2.5 4.7 4.7 0 0 0-2-1.4 7.1 7.1 0 0 0-2.8-.5 8.3 8.3 0 0 0-3.1.5 6 6 0 0 0-2.2 1.5 7 7 0 0 0-1.3 2 10.6 10.6 0 0 0-.8 4.1 10 10 0 0 0 1 4.5 6 6 0 0 0 2.7 2.8 8.4 8.4 0 0 0 3.8.9 9 9 0 0 0 3.4-.7 8.8 8.8 0 0 0 2.5-1.5v-3.7z"></path>
                          <path d="M18.8 278.1q0-5.9 3.2-9.2a10.8 10.8 0 0 1 8.2-3.4 11.4 11.4 0 0 1 5.9 1.6 10.3 10.3 0 0 1 4 4.4 14.2 14.2 0 0 1 1.4 6.3 14 14 0 0 1-1.5 6.5 10 10 0 0 1-4 4.3 11.8 11.8 0 0 1-5.9 1.5 11.2 11.2 0 0 1-6-1.6 10.4 10.4 0 0 1-4-4.4 13.4 13.4 0 0 1-1.3-6zm3.2 0a9.5 9.5 0 0 0 2.3 6.8 7.6 7.6 0 0 0 5.8 2.5A7.6 7.6 0 0 0 36 285q2.3-2.5 2.3-7a12.2 12.2 0 0 0-1-5.1 7.5 7.5 0 0 0-2.9-3.4 7.8 7.8 0 0 0-4.2-1.2 8 8 0 0 0-5.8 2.3q-2.4 2.3-2.4 7.7zM45.6 289.7v-23.8h9a21.6 21.6 0 0 1 3.5.3 7.2 7.2 0 0 1 3 1 5.8 5.8 0 0 1 1.9 2.4 7.7 7.7 0 0 1-1.2 8.3q-2 2.1-7 2.1h-6.1v9.7zm3.1-12.5h6.1q3.1 0 4.4-1.1a4 4 0 0 0 1.3-3.2 4.3 4.3 0 0 0-.8-2.6 3.5 3.5 0 0 0-2-1.4 13.4 13.4 0 0 0-3-.2h-6zM67.7 289.7v-23.8h9a21.6 21.6 0 0 1 3.6.3 7.2 7.2 0 0 1 3 1 5.8 5.8 0 0 1 1.8 2.4 7.7 7.7 0 0 1-1.2 8.3q-2 2.1-7 2.1h-6v9.7zm3.1-12.5H77q3 0 4.3-1.1a4 4 0 0 0 1.3-3.2 4.3 4.3 0 0 0-.8-2.6 3.5 3.5 0 0 0-2-1.4 13.4 13.4 0 0 0-2.9-.2h-6zM88.9 278.1q0-5.9 3.2-9.2a10.8 10.8 0 0 1 8.2-3.4 11.4 11.4 0 0 1 5.9 1.6 10.3 10.3 0 0 1 4 4.4 14.2 14.2 0 0 1 1.4 6.3 14 14 0 0 1-1.4 6.5 10 10 0 0 1-4.2 4.3 11.8 11.8 0 0 1-5.7 1.5 11.2 11.2 0 0 1-6-1.6 10.4 10.4 0 0 1-4-4.4 13.4 13.4 0 0 1-1.4-6zm3.2 0a9.5 9.5 0 0 0 2.3 6.8 8 8 0 0 0 11.7 0q2.3-2.5 2.3-7a12.2 12.2 0 0 0-1-5.1 7.5 7.5 0 0 0-2.9-3.4 7.8 7.8 0 0 0-4.2-1.2 8 8 0 0 0-5.8 2.3q-2.4 2.3-2.4 7.7zM115.7 289.7v-23.8h10.6a14 14 0 0 1 4.8.7 5.3 5.3 0 0 1 2.6 2.2 6.7 6.7 0 0 1 1 3.6 6 6 0 0 1-1.6 4.3 8.3 8.3 0 0 1-5 2.2 8.1 8.1 0 0 1 1.8 1.2 16.4 16.4 0 0 1 2.6 3.1l4.2 6.5h-4l-3.1-5-2.3-3.2a7.1 7.1 0 0 0-1.6-1.6 4.7 4.7 0 0 0-1.4-.6 9.4 9.4 0 0 0-1.8-.2H119v10.6zm3.2-13.3h6.7a10.1 10.1 0 0 0 3.4-.4 3.6 3.6 0 0 0 1.8-1.5 3.9 3.9 0 0 0 .7-2 3.5 3.5 0 0 0-1.2-2.9 5.7 5.7 0 0 0-4-1H119zM145 289.7v-21h-7.7V266H156v2.8h-7.9v21zM175 266h3v13.7a16.2 16.2 0 0 1-.7 5.7 7 7 0 0 1-3 3.4 10.4 10.4 0 0 1-5.5 1.3 11.4 11.4 0 0 1-5.5-1.2 6.6 6.6 0 0 1-3-3.3 15.9 15.9 0 0 1-1-6V266h3.2v13.7a13.4 13.4 0 0 0 .6 4.6 4.4 4.4 0 0 0 2 2.3 6.9 6.9 0 0 0 3.4.8q3.5 0 5-1.6t1.4-6zM183.3 289.7v-23.8h3.2l12.5 18.7v-18.7h3v23.8h-3.2L186.3 271v18.7zM207.8 289.7v-23.8h3.2v23.8zM222.6 289.7v-21h-7.9V266h18.9v2.8h-7.9v21zM243.5 289.7v-10l-9.2-13.8h3.8l4.7 7.2 2.4 4 2.6-4.2 4.6-7h3.7l-9.5 13.7v10.1z"></path>
                        </g>
                      </svg>
                      <span class="VisuallyHidden-c11n-8-111-2__sc-t8tewe-0 bARXoy">Equal Housing Opportunity</span>
                    </a>
                  </span>
                </li>
              </ul>
            </div>
            <img src="https://www.zillowstatic.com/s3/pfs/static/footer-art.svg" type="image/svg+xml" alt="Footer art" focusable="false" height="160" width="1200" class="pfs__kizoah-0 hreAJS" loading="lazy" decoding="async" />
            <script src="https://www.zillowstatic.com/s3/pfs/datagrailBanner-a0d378b68eb95adf7915.js"></script>
          </footer>
        </div>
      </div> */}
    </div>
  );
};

export default ZillowProperty;
